/**
 * Widget copy and language handling.
 *
 * Arabic is a first-class language here, not a translation layer bolted on:
 * every string exists in both, and `direction` drives a real RTL layout rather
 * than a mirrored LTR one.
 */

import { ApiErrorKind } from './api';
import { ResolvedLanguage } from './types';

interface Strings {
    title: string;
    open: string;
    close: string;
    minimize: string;
    clear: string;
    send: string;
    placeholder: string;
    thinking: string;
    suggestionsLabel: string;
    sourcesLabel: string;
    conversationLabel: string;
    emptyState: string;
    retry: string;
    poweredBy: string;
    statusOnline: string;
    statusOffline: string;
    statusConnecting: string;
    openPage: string;
    errors: Record<ApiErrorKind, string>;
}

const EN: Strings = {
    title: 'Assistant',
    open: 'Open the assistant',
    close: 'Close the assistant',
    minimize: 'Minimize the assistant',
    clear: 'Clear the conversation',
    send: 'Send message',
    placeholder: 'Ask a question…',
    thinking: 'Looking that up…',
    suggestionsLabel: 'Suggested questions',
    sourcesLabel: 'Sources',
    conversationLabel: 'Conversation',
    emptyState: 'Ask a question below, or tap a suggestion.',
    retry: 'Try again',
    poweredBy: '',
    statusOnline: 'Online',
    statusOffline: 'Offline',
    statusConnecting: 'Connecting…',
    openPage: 'Open page',
    errors: {
        offline:
            "I can't reach the assistant right now. Check your connection and try again.",
        timeout: 'That took too long to answer. Please try again.',
        'rate-limited':
            "You've sent a lot of messages. Please wait a moment and try again.",
        unavailable:
            "Sorry, I'm unable to process your request right now. Please try again.",
        'not-found': 'This assistant is not configured for this page.',
        invalid: "I couldn't process that message. Try rephrasing it.",
        unknown: 'Sorry, something went wrong. Please try again.'
    }
};

const AR: Strings = {
    title: 'المساعد',
    open: 'فتح المساعد',
    close: 'إغلاق المساعد',
    minimize: 'تصغير المساعد',
    clear: 'مسح المحادثة',
    send: 'إرسال الرسالة',
    placeholder: 'اكتب سؤالك…',
    thinking: 'جارٍ البحث…',
    suggestionsLabel: 'أسئلة مقترحة',
    sourcesLabel: 'المصادر',
    conversationLabel: 'المحادثة',
    emptyState: 'اطرح سؤالاً أو اختر اقتراحاً.',
    retry: 'حاول مرة أخرى',
    poweredBy: '',
    statusOnline: 'متصل',
    statusOffline: 'غير متصل',
    statusConnecting: 'جارٍ الاتصال…',
    openPage: 'فتح الصفحة',
    errors: {
        offline: 'لا يمكنني الوصول إلى المساعد حالياً. تحقق من اتصالك وحاول مرة أخرى.',
        timeout: 'استغرقت الإجابة وقتاً طويلاً. يرجى المحاولة مرة أخرى.',
        'rate-limited':
            'لقد أرسلت رسائل كثيرة. يرجى الانتظار قليلاً ثم المحاولة مرة أخرى.',
        unavailable:
            'عذراً، لا يمكنني معالجة طلبك في الوقت الحالي. يرجى المحاولة مرة أخرى.',
        'not-found': 'هذا المساعد غير مُهيّأ لهذه الصفحة.',
        invalid: 'لم أتمكن من معالجة هذه الرسالة. حاول إعادة صياغتها.',
        unknown: 'عذراً، حدث خطأ ما. يرجى المحاولة مرة أخرى.'
    }
};

export function strings(language: ResolvedLanguage): Strings {
    return language === 'ar' ? AR : EN;
}

export function direction(language: ResolvedLanguage): 'rtl' | 'ltr' {
    return language === 'ar' ? 'rtl' : 'ltr';
}

/**
 * Arabic blocks, built from code points.
 *
 * The final block ends at U+FEFF, a zero-width no-break space that is
 * invisible in source and rejected as irregular whitespace by the linter.
 */
const ARABIC_RANGE = new RegExp(
    '[' +
        [
            [0x0600, 0x06ff],
            [0x0750, 0x077f],
            [0x08a0, 0x08ff],
            [0xfb50, 0xfdff],
            [0xfe70, 0xfeff]
        ]
            .map(
                ([from, to]) =>
                    String.fromCodePoint(from as number) +
                    '-' +
                    String.fromCodePoint(to as number)
            )
            .join('') +
        ']'
);

/**
 * Resolves 'auto'.
 *
 * The host page's own lang attribute is the strongest signal available before
 * the user has typed anything - it is what the site's authors declared.
 */
export function resolveLanguage(
    requested: 'en' | 'ar' | 'auto',
    fallback: ResolvedLanguage = 'en'
): ResolvedLanguage {
    if (requested !== 'auto') {
        return requested;
    }

    try {
        const declared = (
            document.documentElement.getAttribute('lang') ??
            navigator.language ??
            ''
        ).toLowerCase();

        if (declared.startsWith('ar')) {
            return 'ar';
        }
        if (declared.startsWith('en')) {
            return 'en';
        }
    } catch {
        // Fall through to the configured default.
    }

    return fallback;
}

/** Detects the language of a typed message so a reply can match it. */
export function detectMessageLanguage(
    text: string,
    fallback: ResolvedLanguage
): ResolvedLanguage {
    let arabic = 0;
    let latin = 0;
    for (const char of text) {
        if (ARABIC_RANGE.test(char)) {
            arabic += 1;
        } else if (/[A-Za-z]/.test(char)) {
            latin += 1;
        }
    }
    if (arabic === 0 && latin === 0) {
        return fallback;
    }
    return arabic > latin ? 'ar' : 'en';
}
